import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@threatlens/design-system';
import { api, ApiError, getSession, getSessionUserId, isSignedIn, type AgentCitation, type AgentSchedule, type Wallet } from '../lib/api.js';
import { asArray } from '../lib/as-array.js';
import { errMsg } from '../lib/errors.js';
import { MarkdownLite } from './MarkdownLite.js';

type Mode = 'quick' | 'advanced';

interface ChatTurn {
  role: 'user' | 'agent';
  text: string;
  mode?: Mode;
  citations?: AgentCitation[];
  credits?: number;
  model?: string;
}

interface Thread {
  id: string;
  title: string;
  mode: Mode;
  turns: ChatTurn[];
  createdAt: number;
  updatedAt: number;
}

const MAX_THREADS = 50;
const newId = () => `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

/**
 * Stable per-user key so one browser's users don't share chat history.
 *
 * Reads the SERVER session rather than decoding the token. In cookie mode the token is unreadable
 * and `getSession().accessToken` is the sentinel string 'cookie', so the old JWT decode threw on
 * every call and fell through to the constant `'user'` - which made the key
 * `ma_agent_threads_user` for everyone, and two accounts sharing a browser could read each other's
 * conversations. Exactly the thing this function's name promises not to do.
 */
function currentUserId(): string {
  if (!isSignedIn()) return 'anon';
  // Null means the session lookup has not resolved yet. `pending` keeps that state in its own
  // bucket rather than pouring it into the shared `user` key the bug created.
  return getSessionUserId() ?? 'pending';
}

function historyKey(userId: string): string {
  return `ma_agent_threads_${userId}`;
}
function loadThreads(userId: string): Thread[] {
  try {
    const raw = localStorage.getItem(historyKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Thread[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function saveThreads(userId: string, threads: Thread[]): void {
  try {
    localStorage.setItem(historyKey(userId), JSON.stringify(threads.slice(0, MAX_THREADS)));
  } catch {
    /* quota / disabled storage - history just won't persist */
  }
}

/**
 * Global MalwareAnalyzer Agent chatbot: a floating launcher available on every page (for signed-in
 * users) that expands into a chat panel, replacing the old dedicated /agent page. Conversations are
 * kept as per-user threads in localStorage so users can browse and reopen their history. Quick =
 * internal RAG + DeepSeek; Advanced = + external Atlas/BlazeAI intel over SSE. A live token meter
 * debits the wallet; exhaustion drives the Billing upsell.
 */
export function AgentChatWidget() {
  const { accessToken } = getSession();
  const signedIn = !!accessToken;
  const userId = currentUserId();

  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [mode, setMode] = useState<Mode>('quick');
  const [input, setInput] = useState('');
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exhausted, setExhausted] = useState(false);
  const [schedules, setSchedules] = useState<AgentSchedule[]>([]);

  const turnsRef = useRef<ChatTurn[]>([]);
  const activeIdRef = useRef<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    turnsRef.current = turns;
  }, [turns]);
  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  // Load per-user history + wallet the first time the panel opens (and when the user changes).
  useEffect(() => {
    if (!open || !signedIn) return;
    let cancelled = false;
    setThreads(loadThreads(userId));
    api
      .getWallet()
      .then((r) => {
        if (!cancelled) setWallet(r.wallet);
      })
      .catch(() => undefined);
    api
      .listSchedules()
      .then((r) => {
        if (!cancelled) setSchedules(asArray(r.schedules));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, signedIn, userId]);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns, open, showHistory]);

  const persistTurns = useCallback(
    (nextTurns: ChatTurn[], curMode: Mode) => {
      if (nextTurns.length === 0) return;
      const firstUser = nextTurns.find((t) => t.role === 'user')?.text ?? 'Conversation';
      const title = firstUser.slice(0, 80);
      const id = activeIdRef.current ?? newId();
      if (!activeIdRef.current) {
        activeIdRef.current = id;
        setActiveId(id);
      }
      setThreads((prev) => {
        const now = Date.now();
        const existing = prev.find((t) => t.id === id);
        const thread: Thread = {
          id,
          title,
          mode: curMode,
          turns: nextTurns,
          createdAt: existing?.createdAt ?? now,
          updatedAt: now,
        };
        const next = [thread, ...prev.filter((t) => t.id !== id)].slice(0, MAX_THREADS);
        saveThreads(userId, next);
        return next;
      });
    },
    [userId],
  );

  const askWith = useCallback(
    async (raw: string) => {
      const query = raw.trim();
      if (!query || busy) return;
      setBusy(true);
      setError(null);
      setProgress(null);
      const withUser: ChatTurn[] = [...turnsRef.current, { role: 'user', text: query }];
      turnsRef.current = withUser;
      setTurns(withUser);
      setInput('');
      try {
        const res =
          mode === 'advanced'
            ? await api.agentQueryStream(query, mode, (stage) => setProgress(stage))
            : await api.agentQuery(query, mode);
        const withAgent: ChatTurn[] = [
          ...turnsRef.current,
          { role: 'agent', text: res.answer, mode: res.mode, citations: res.citations, credits: res.usage.credits, model: res.model },
        ];
        turnsRef.current = withAgent;
        setTurns(withAgent);
        persistTurns(withAgent, mode);
        if (res.wallet.remaining !== null) {
          setWallet((w) => (w ? { ...w, remaining: res.wallet.remaining!, used: w.granted - res.wallet.remaining! } : w));
        }
      } catch (err) {
        if (err instanceof ApiError && (err.status === 402 || (err.detail as { upsell?: boolean })?.upsell)) {
          setExhausted(true);
          setError("You're out of credits. Top up or upgrade to keep using MalwareAnalyzer Agent.");
        } else {
          setError(errMsg(err));
        }
      } finally {
        setBusy(false);
        setProgress(null);
      }
    },
    [busy, mode, persistTurns],
  );

  // Open-from-anywhere WITHOUT a cross-component handoff: this widget watches the router location
  // itself. Any navigation to /agent (the nav link, report/scan "Ask agent" deep links with ?q=, or a
  // cold direct load / bookmark) is detected here, so the widget opens its OWN panel (the same instance
  // that renders it - no mount/subscription/event race), auto-asks a deep-linked question, and then
  // redirects away so there is no blank dedicated page. This replaced an event/store handoff that
  // reliably lost the race on a cold direct load of /agent.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!signedIn || location.pathname !== '/agent') return;
    const q = new URLSearchParams(location.search).get('q') ?? '';
    // Defer the whole open+redirect to a macrotask. On a cold direct load /agent is the INITIAL route,
    // so this effect fires from the widget's own mount commit - and a setOpen(true) issued during that
    // initial mount did not stick (the panel stayed closed), whereas the identical update issued after
    // mount (in-app navigation into /agent) opens reliably. Running it on a macrotask makes the cold
    // load behave like the working post-mount case: the widget is fully settled when it opens, then we
    // leave /agent so there is no blank dedicated page.
    const t = setTimeout(() => {
      setOpen(true);
      setShowHistory(false);
      if (q.trim()) {
        turnsRef.current = [];
        activeIdRef.current = null;
        setActiveId(null);
        setTurns([]);
        setExhausted(false);
        void askWith(q);
      } else {
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (window.history.length > 1) navigate(-1);
      else navigate('/', { replace: true });
    }, 0);
    return () => clearTimeout(t);
  }, [location.pathname, location.search, signedIn, askWith, navigate]);

  const newChat = useCallback(() => {
    turnsRef.current = [];
    activeIdRef.current = null;
    setActiveId(null);
    setTurns([]);
    setError(null);
    setExhausted(false);
    setShowHistory(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const openThread = useCallback((t: Thread) => {
    turnsRef.current = t.turns;
    activeIdRef.current = t.id;
    setActiveId(t.id);
    setTurns(t.turns);
    setMode(t.mode);
    setShowHistory(false);
    setError(null);
    setExhausted(false);
  }, []);

  const deleteThread = useCallback(
    (id: string) => {
      setThreads((prev) => {
        const next = prev.filter((t) => t.id !== id);
        saveThreads(userId, next);
        return next;
      });
      if (activeIdRef.current === id) newChat();
    },
    [userId, newChat],
  );

  const scheduleCurrent = useCallback(async () => {
    const query = input.trim();
    if (!query) return;
    try {
      await api.createSchedule(query.slice(0, 60), query, mode, 1440);
      setInput('');
      const r = await api.listSchedules();
      setSchedules(asArray(r.schedules));
      setError(null);
    } catch (err) {
      setError(errMsg(err));
    }
  }, [input, mode]);

  if (!signedIn) return null;

  // Collapsed launcher.
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        aria-label="Open MalwareAnalyzer Agent"
        className="fixed bottom-4 right-4 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-cta text-white shadow-lg transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-focus"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      </button>
    );
  }

  const remaining = wallet?.remaining ?? 0;
  const pct = wallet && wallet.granted > 0 ? Math.max(0, Math.min(100, (remaining / wallet.granted) * 100)) : 0;

  return (
    <div className="fixed bottom-4 right-4 z-[60] flex h-[min(40rem,calc(100dvh-5rem))] w-[min(26rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border border-border-default bg-bg-base shadow-2xl">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-border-subtle bg-bg-surface px-3 py-2">
        <span className="text-accent-500">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-text-primary">MalwareAnalyzer Agent</div>
        </div>
        <button
          onClick={() => setShowHistory((v) => !v)}
          title="Chat history"
          aria-label="Chat history"
          className={`rounded p-1 ${showHistory ? 'text-accent-500' : 'text-text-muted hover:text-text-primary'}`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M3 3v5h5" />
            <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
            <path d="M12 7v5l4 2" />
          </svg>
        </button>
        <button onClick={newChat} title="New chat" aria-label="New chat" className="rounded p-1 text-text-muted hover:text-text-primary">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
        <button onClick={() => setOpen(false)} title="Close" aria-label="Close" className="rounded p-1 text-text-muted hover:text-text-primary">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Mode + wallet */}
      <div className="flex items-center justify-between gap-2 border-b border-border-subtle px-3 py-1.5">
        <div className="flex gap-1 rounded-md bg-bg-surface p-0.5">
          {(['quick', 'advanced'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`rounded px-2 py-1 text-xs capitalize transition-colors ${mode === m ? 'bg-cta text-cta-fg' : 'text-text-secondary hover:text-text-primary'}`}
            >
              {m === 'quick' ? 'Quick' : 'Advanced'}
            </button>
          ))}
        </div>
        {wallet && (
          <div className="min-w-28 flex-1">
            <div className="flex justify-between text-[10px] text-text-muted">
              <span>Credits</span>
              <span className="font-mono">{remaining.toLocaleString()}</span>
            </div>
            <div className="mt-0.5 h-1.5 w-full rounded-full bg-bg-surface">
              <div className={`h-1.5 rounded-full ${pct < 10 ? 'bg-status-danger' : 'bg-accent-500'}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Body: history list OR chat transcript */}
      {showHistory ? (
        <div className="flex-1 overflow-y-auto p-2">
          <div className="mb-1 px-1 text-xs font-semibold text-text-secondary">Recent conversations</div>
          {threads.length === 0 && <p className="px-1 py-2 text-xs text-text-muted">No conversations yet.</p>}
          <div className="space-y-1">
            {threads.map((t) => (
              <div key={t.id} className={`group flex items-center gap-2 rounded-md px-2 py-1.5 ${t.id === activeId ? 'bg-accent-500/10' : 'hover:bg-bg-surface'}`}>
                <button onClick={() => openThread(t)} className="min-w-0 flex-1 text-left">
                  <div className="truncate text-sm text-text-primary">{t.title}</div>
                  <div className="text-[10px] text-text-muted">
                    {new Date(t.updatedAt).toLocaleString()} · {t.turns.filter((x) => x.role === 'agent').length} repl{t.turns.filter((x) => x.role === 'agent').length === 1 ? 'y' : 'ies'} · {t.mode}
                  </div>
                </button>
                <button onClick={() => deleteThread(t.id)} title="Delete" className="text-text-muted opacity-0 transition-opacity hover:text-status-danger group-hover:opacity-100">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden>
                    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

          {schedules.length > 0 && (
            <div className="mt-3">
              <div className="mb-1 px-1 text-xs font-semibold text-text-secondary">Scheduled queries</div>
              <div className="space-y-1">
                {schedules.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 px-2 py-1 text-xs text-text-secondary">
                    <span className="min-w-0 flex-1 truncate">{s.name}</span>
                    <span className="text-text-muted">every {Math.round(s.intervalMinutes / 60)}h</span>
                    <button
                      className="text-status-danger hover:underline"
                      onClick={() => void api.deleteSchedule(s.id).then(() => api.listSchedules()).then((r) => setSchedules(asArray(r.schedules))).catch(() => undefined)}
                    >
                      remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-3">
          {turns.length === 0 && (
            <div className="m-auto max-w-xs text-center text-text-muted">
              <p className="text-sm">Ask about samples, families, IOCs, and campaigns across the platform corpus.</p>
              <p className="mt-2 text-xs">Try: &ldquo;Summarize recent ransomware families&rdquo; or &ldquo;What IOCs are linked to this hash?&rdquo;</p>
            </div>
          )}
          {turns.map((t, i) => (
            <div key={i} className={`max-w-[88%] rounded-lg p-2.5 text-sm ${t.role === 'user' ? 'self-end bg-accent-500/20 text-text-primary' : 'self-start bg-bg-surface text-text-primary'}`}>
              {t.role === 'agent' ? <MarkdownLite text={t.text} /> : <div className="whitespace-pre-wrap">{t.text}</div>}
              {t.role === 'agent' && (
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[10px] text-text-muted">
                  {t.model && <span className="rounded bg-bg-base px-1.5 py-0.5">{t.model}</span>}
                  {typeof t.credits === 'number' && <span>{t.credits.toLocaleString()} credit{t.credits === 1 ? '' : 's'}</span>}
                  {t.citations && t.citations.length > 0 && (
                    <span>· {t.citations.length} source{t.citations.length > 1 ? 's' : ''}: {t.citations.slice(0, 3).map((c) => c.label).join(', ')}</span>
                  )}
                </div>
              )}
            </div>
          ))}
          {busy && <div className="self-start rounded-lg bg-bg-surface p-2.5 text-sm text-text-muted">{progress ?? 'Thinking…'}</div>}
          <div ref={endRef} />
        </div>
      )}

      {error && (
        <div className={`mx-3 mb-1 rounded border p-2 text-xs ${exhausted ? 'border-accent-500/40 bg-accent-500/10 text-text-secondary' : 'border-status-danger/30 bg-status-danger/10 text-status-danger'}`}>
          {error}{' '}
          {exhausted && <Link to="/billing" onClick={() => setOpen(false)} className="font-medium text-accent-400 underline">Top up now →</Link>}
        </div>
      )}

      {/* Composer */}
      {!showHistory && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void askWith(input);
          }}
          className="flex items-center gap-2 border-t border-border-subtle p-2"
        >
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask MalwareAnalyzer Agent…"
            className="min-w-0 flex-1 rounded-md border border-border-default bg-bg-base px-3 py-2 text-sm text-text-primary outline-none focus:border-accent-500"
          />
          <button
            type="button"
            title="Schedule this as a recurring query"
            onClick={() => void scheduleCurrent()}
            disabled={!input.trim()}
            className="rounded p-1.5 text-text-muted hover:text-text-primary disabled:opacity-40"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
          </button>
          <Button type="submit" size="sm" loading={busy} disabled={!input.trim()}>Send</Button>
        </form>
      )}
    </div>
  );
}
